import React, { useState } from 'react';
import './TabStyles.css';

// Accordion component for collapsible sections
const AccordionSection = ({ title, subtitle, isExpanded, onToggle, children, icon, count }) => {
    return (
        <div className="accordion-section">
            <div className="accordion-header" onClick={onToggle}>
                <div className="accordion-header-left">
                    {icon && <span className="accordion-icon">{icon}</span>}
                    <div>
                        <h4 className="accordion-title">{title}</h4>
                        {subtitle && <p className="accordion-subtitle">{subtitle}</p>}
                    </div>
                </div>
                <div className="accordion-header-right">
                    {count !== undefined && count > 0 && (
                        <span className="accordion-count">{count}</span>
                    )}
                    <span className="accordion-chevron">{isExpanded ? '▼' : '▶'}</span>
                </div>
            </div>
            {isExpanded && (
                <div className="accordion-content">
                    {children}
                </div>
            )}
        </div>
    );
};

const ExplainabilityTab = ({ candidate }) => {
    // State for accordion sections
    const [expandedSections, setExpandedSections] = useState({
        resolvedProfile: false,
        scoreBreakdown: false,
        dimensionExplanations: false,
        processingTimeline: false,
        agentDetails: false,
        citations: false,
        reasoningChains: false,
        decisionPoints: false,
        enhancedDecisions: false
    });

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Parse agent_metadata if it's a string
    const getAgentMetadata = () => {
        if (!candidate.agent_metadata) return null;
        
        if (typeof candidate.agent_metadata === 'string') {
            try {
                return JSON.parse(candidate.agent_metadata);
            } catch (e) {
                console.warn('[ExplainabilityTab] Failed to parse agent_metadata as JSON:', e);
                return null;
            }
        }
        
        return candidate.agent_metadata;
    };

    const agentMetadata = getAgentMetadata();
    const auditTrail = agentMetadata?.audit_trail;

    // Parse rag_analysis if it's a string
    const getRagAnalysis = () => {
        if (!candidate.rag_analysis) return null;
        
        if (typeof candidate.rag_analysis === 'string') {
            try {
                return JSON.parse(candidate.rag_analysis);
            } catch (e) {
                console.warn('[ExplainabilityTab] Failed to parse rag_analysis as JSON:', e);
                return null;
            }
        }
        
        return candidate.rag_analysis;
    };

    const ragAnalysis = getRagAnalysis();
    const evalResult = ragAnalysis?.evaluation_result;

    // Extract comprehensive data from audit_trail
    const scoreExplanations = auditTrail?.score_explanations || [];
    const reasoningChains = scoreExplanations.filter(exp => exp.reasoning_chain);
    const ragContextReferences = auditTrail?.rag_context_references || [];
    const agentOutputs = auditTrail?.agent_outputs || [];
    const decisionPoints = auditTrail?.decision_points || [];
    const categoryExplanations = auditTrail?.category_explanations || [];
    
    // Enhanced explainability fields
    const jdExtractionDecisions = auditTrail?.jd_extraction_decisions || [];
    const evaluationModeDecision = auditTrail?.evaluation_mode_decision;
    const scoreCalculationDetails = auditTrail?.score_calculation_details;
    const substitutabilityDecisions = auditTrail?.substitutability_decisions || [];
    const parallelExecutionDecisions = auditTrail?.parallel_execution_decisions;
    const processingTimeline = auditTrail?.processing_timeline || agentMetadata?.processing_timeline || [];
    const totalProcessingTime = auditTrail?.total_processing_time_ms || agentMetadata?.total_processing_time_ms;
    
    // Resolved Scoring Profile
    const resolvedScoringProfile = auditTrail?.resolved_scoring_profile;

    // Get score breakdown
    const getScoreBreakdown = () => {
        if (scoreCalculationDetails) {
            return {
                overall: scoreCalculationDetails.final_score,
                calibrated: evalResult?.calibrated_score,
                capability: evalResult?.capability_score,
                fit: evalResult?.fit_score,
                weights: scoreCalculationDetails.dimension_weights,
                dimensions: scoreCalculationDetails.dimension_scores,
                formula: scoreCalculationDetails.formula,
                calculationSteps: scoreCalculationDetails.calculation_steps || []
            };
        } else if (evalResult) {
            return {
                overall: evalResult.overall_score,
                calibrated: evalResult.calibrated_score,
                capability: evalResult.capability_score,
                fit: evalResult.fit_score,
                weights: evalResult.score_weights,
                dimensions: {
                    skills: evalResult.skills_score?.score,
                    experience: evalResult.experience_score?.score,
                    education: evalResult.education_score?.score,
                    projects: evalResult.projects_score?.score
                }
            };
        }
        return null;
    };

    // Helper function to derive weight source explanation
    const getWeightSource = (dimension, weight, profile) => {
        const weightPercent = Math.round(weight * 100);
        
        // Check if we can infer from weight values and archetype
        if (dimension === 'experience' && weightPercent >= 45) {
            return 'JD requires 5+ years';
        } else if (dimension === 'experience' && weightPercent <= 30) {
            return 'JD requires <3 years';
        } else if (dimension === 'skills' && weightPercent >= 45) {
            return 'High skill density';
        } else if (dimension === 'education' && weightPercent >= 10) {
            return 'Explicit degree requirement';
        } else if (dimension === 'projects' && weightPercent >= 10) {
            return 'Portfolio/projects emphasized';
        } else if (dimension === 'education' && weightPercent < 5) {
            return 'Not emphasized';
        } else if (dimension === 'projects' && weightPercent < 5) {
            return 'Not emphasized';
        }
        
        return 'Archetype default';
    };

    const breakdown = getScoreBreakdown();
    const hasData = auditTrail || breakdown || scoreExplanations.length > 0 || 
                    ragContextReferences.length > 0 || agentOutputs.length > 0 || 
                    decisionPoints.length > 0 || processingTimeline.length > 0 || 
                    resolvedScoringProfile;

    return (
        <div className="tab-content explainability-tab">
            <h2 className="section-header">AUDIT & EXPLAINABILITY</h2>
            <p className="section-description" style={{ marginTop: '-1rem', marginBottom: '2rem', color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
                Deep technical view of how scores were calculated, evidence used, and decisions made.
            </p>

            {!hasData && (
                <div className="no-data-message">
                    <p>Explainability data not available for this candidate.</p>
                </div>
            )}

            {hasData && (
                <div className="explainability-sections">
                    {/* Resolved Scoring Profile */}
                    {resolvedScoringProfile && (
                        <AccordionSection
                            title="Resolved Scoring Profile"
                            subtitle="JD-derived weights used for this evaluation"
                            icon="📋"
                            isExpanded={expandedSections.resolvedProfile}
                            onToggle={() => toggleSection('resolvedProfile')}
                        >
                            <div className="resolved-profile-content">
                                {/* Role Archetype Info */}
                                {resolvedScoringProfile.role_archetype && (
                                    <div className="profile-archetype-info">
                                        <h5>Role Context</h5>
                                        <div className="archetype-details">
                                            <div className="archetype-item">
                                                <span className="archetype-label">Archetype:</span>
                                                <span className="archetype-value">{resolvedScoringProfile.role_archetype.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                                            </div>
                                            {resolvedScoringProfile.role_family && (
                                                <div className="archetype-item">
                                                    <span className="archetype-label">Role Family:</span>
                                                    <span className="archetype-value">{resolvedScoringProfile.role_family}</span>
                                                </div>
                                            )}
                                            {resolvedScoringProfile.level && (
                                                <div className="archetype-item">
                                                    <span className="archetype-label">Level:</span>
                                                    <span className="archetype-value">{resolvedScoringProfile.level}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Weights Table */}
                                {resolvedScoringProfile.weights && (
                                    <div className="resolved-profile-table-section">
                                        <h5>Dimension Weights — This Job</h5>
                                        <div className="resolved-profile-table">
                                            <div className="table-header">
                                                <div className="table-col-dimension">Dimension</div>
                                                <div className="table-col-weight">Weight</div>
                                                <div className="table-col-source">Source</div>
                                            </div>
                                            {Object.entries(resolvedScoringProfile.weights).map(([dimension, weight]) => {
                                                const weightPercent = Math.round(weight * 100);
                                                const source = getWeightSource(dimension, weight, resolvedScoringProfile);
                                                return (
                                                    <div key={dimension} className="table-row">
                                                        <div className="table-col-dimension">
                                                            <span className="dimension-name-capitalized">
                                                                {dimension.charAt(0).toUpperCase() + dimension.slice(1)}
                                                            </span>
                                                        </div>
                                                        <div className="table-col-weight">
                                                            <span className="weight-percent">{weightPercent}%</span>
                                                        </div>
                                                        <div className="table-col-source">
                                                            <span className="weight-source">{source}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Step-by-step Explanation */}
                                <div className="weight-resolution-steps">
                                    <h5>How Weights Were Resolved</h5>
                                    <ol className="resolution-steps-list">
                                        <li>
                                            <strong>Role archetype selected</strong><br />
                                            <span className="step-detail">Based on the role (e.g., {resolvedScoringProfile.role_archetype?.replace(/_/g, ' ') || 'matched archetype'})</span>
                                        </li>
                                        <li>
                                            <strong>Job description analyzed</strong><br />
                                            <span className="step-detail">We extract experience requirements, skill density, education needs, and delivery signals</span>
                                        </li>
                                        <li>
                                            <strong>Bounded adjustments applied</strong><br />
                                            <span className="step-detail">Weights are adjusted within predefined limits to reflect job priorities</span>
                                        </li>
                                        <li>
                                            <strong>Weights normalized</strong><br />
                                            <span className="step-detail">Final weights always sum to 100%</span>
                                        </li>
                                    </ol>
                                </div>

                                {/* Governance Note */}
                                <div className="governance-note">
                                    <span className="governance-icon">ℹ️</span>
                                    <p>
                                        <strong>Governance:</strong> Weights are constrained within safe bounds defined by the role archetype to ensure consistency and fairness across evaluations.
                                    </p>
                                </div>
                            </div>
                        </AccordionSection>
                    )}

                    {/* Score Calculation Breakdown */}
                    {breakdown && (
                        <AccordionSection
                            title="Score Calculation Breakdown"
                            subtitle="How the final scores were computed"
                            icon="🧮"
                            isExpanded={expandedSections.scoreBreakdown}
                            onToggle={() => toggleSection('scoreBreakdown')}
                        >
                            <div className="score-breakdown-content">
                                <div className="breakdown-summary">
                                    <div className="summary-item">
                                        <span className="summary-label">Overall Score</span>
                                        <span className="summary-value">{breakdown.overall?.toFixed(1) || 'N/A'}</span>
                                    </div>
                                    {breakdown.calibrated !== undefined && (
                                        <div className="summary-item">
                                            <span className="summary-label">Calibrated Score</span>
                                            <span className="summary-value">{breakdown.calibrated.toFixed(1)}</span>
                                        </div>
                                    )}
                                    {breakdown.capability !== undefined && (
                                        <div className="summary-item">
                                            <span className="summary-label">Capability</span>
                                            <span className="summary-value">{breakdown.capability.toFixed(1)}</span>
                                        </div>
                                    )}
                                    {breakdown.fit !== undefined && (
                                        <div className="summary-item">
                                            <span className="summary-label">Fit</span>
                                            <span className="summary-value">{breakdown.fit.toFixed(1)}</span>
                                        </div>
                                    )}
                                </div>

                                {breakdown.formula && (
                                    <div className="formula-display">
                                        <div className="formula-label">
                                            <strong>Formula:</strong>
                                            <span className="formula-note">Derived after JD analysis — not a system default</span>
                                        </div>
                                        <code>{breakdown.formula}</code>
                                    </div>
                                )}

                                {breakdown.weights && (
                                    <div className="weights-breakdown">
                                        <h5>Dimension Weights</h5>
                                        <div className="weights-list">
                                            {Object.entries(breakdown.weights).map(([key, value]) => (
                                                <div key={key} className="weight-item">
                                                    <span className="weight-name">{key}</span>
                                                    <span className="weight-value">{Math.round(value * 100)}%</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {breakdown.calculationSteps && breakdown.calculationSteps.length > 0 && (
                                    <div className="calculation-steps">
                                        <h5>Calculation Steps</h5>
                                        {breakdown.calculationSteps.map((step, idx) => (
                                            <div key={idx} className="calculation-step">
                                                <div className="step-header">
                                                    <span className="step-number">Step {step.step_number}</span>
                                                    <span className="step-operation">{step.operation}</span>
                                                    <span className="step-output">{step.output_value.toFixed(2)}</span>
                                                </div>
                                                <p className="step-explanation">{step.explanation}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </AccordionSection>
                    )}

                    {/* Dimension Explanations */}
                    {(scoreExplanations.length > 0 || categoryExplanations.length > 0) && (
                        <AccordionSection
                            title="Dimension Explanations"
                            subtitle="Detailed breakdown by dimension"
                            icon="📊"
                            count={scoreExplanations.length || categoryExplanations.length}
                            isExpanded={expandedSections.dimensionExplanations}
                            onToggle={() => toggleSection('dimensionExplanations')}
                        >
                            <div className="dimension-explanations-content">
                                {scoreExplanations.map((explanation, idx) => (
                                    <div key={idx} className="dimension-explanation-item">
                                        <h5>{explanation.dimension || 'Unknown'}: {explanation.score}/100</h5>
                                        <p className="explanation-text">{explanation.explanation}</p>
                                        {explanation.matched_requirements && explanation.matched_requirements.length > 0 && (
                                            <div className="requirements-section">
                                                <strong>Matched Requirements:</strong>
                                                <ul>
                                                    {explanation.matched_requirements.map((req, rIdx) => (
                                                        <li key={rIdx}>{req}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {explanation.missing_requirements && explanation.missing_requirements.length > 0 && (
                                            <div className="requirements-section">
                                                <strong>Missing Requirements:</strong>
                                                <ul>
                                                    {explanation.missing_requirements.map((req, rIdx) => (
                                                        <li key={rIdx}>{req}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {categoryExplanations.map((explanation, idx) => (
                                    <div key={`cat-${idx}`} className="dimension-explanation-item">
                                        <h5>{explanation.category}</h5>
                                        {explanation.explanation && (
                                            <p className="explanation-text">{explanation.explanation}</p>
                                        )}
                                        {explanation.strengths && explanation.strengths.length > 0 && (
                                            <div className="strengths-section">
                                                <strong>Strengths:</strong>
                                                <ul>
                                                    {explanation.strengths.map((strength, sIdx) => (
                                                        <li key={sIdx}>{strength}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {explanation.weaknesses && explanation.weaknesses.length > 0 && (
                                            <div className="weaknesses-section">
                                                <strong>Weaknesses:</strong>
                                                <ul>
                                                    {explanation.weaknesses.map((weakness, wIdx) => (
                                                        <li key={wIdx}>{weakness}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </AccordionSection>
                    )}

                    {/* Reasoning Chains */}
                    {reasoningChains.length > 0 && (
                        <AccordionSection
                            title="Reasoning Chains"
                            subtitle="Step-by-step reasoning for each dimension"
                            icon="🔗"
                            count={reasoningChains.length}
                            isExpanded={expandedSections.reasoningChains}
                            onToggle={() => toggleSection('reasoningChains')}
                        >
                            <div className="reasoning-chains-content">
                                {reasoningChains.map((explanation, idx) => {
                                    const chain = explanation.reasoning_chain;
                                    return (
                                        <div key={idx} className="reasoning-chain-item">
                                            <h5>{explanation.dimension || chain?.category}: {explanation.score}/100</h5>
                                            {chain?.reasoning_steps && chain.reasoning_steps.map((step, sIdx) => (
                                                <div key={sIdx} className="reasoning-step">
                                                    <div className="step-marker">Step {step.step_number}</div>
                                                    <div className="step-content">
                                                        <p>{step.reasoning}</p>
                                                        {step.evidence && step.evidence.length > 0 && (
                                                            <div className="step-evidence">
                                                                <strong>Evidence:</strong> {step.evidence.join(', ')}
                                                            </div>
                                                        )}
                                                        {step.confidence !== undefined && (
                                                            <div className="step-confidence">
                                                                Confidence: {(step.confidence * 100).toFixed(1)}%
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                            {chain?.conclusion && (
                                                <div className="chain-conclusion">
                                                    <strong>Conclusion:</strong> {chain.conclusion}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </AccordionSection>
                    )}

                    {/* Source Citations */}
                    {ragContextReferences.length > 0 && (
                        <AccordionSection
                            title="Source Citations"
                            subtitle="Evidence sources used in evaluation"
                            icon="📚"
                            count={ragContextReferences.length}
                            isExpanded={expandedSections.citations}
                            onToggle={() => toggleSection('citations')}
                        >
                            <div className="citations-content">
                                {ragContextReferences.map((ref, idx) => (
                                    <div key={idx} className="citation-item">
                                        <div className="citation-header">
                                            <span className="citation-id">Citation #{idx + 1}</span>
                                            <span className="citation-similarity">
                                                Similarity: {(ref.similarity_score * 100).toFixed(1)}%
                                            </span>
                                        </div>
                                        <p className="citation-text">{ref.chunk_text}</p>
                                        {ref.job_requirement && (
                                            <div className="citation-requirement">
                                                <strong>Related Requirement:</strong> {ref.job_requirement}
                                            </div>
                                        )}
                                        {ref.influence && (
                                            <div className="citation-influence">
                                                <strong>Influence:</strong> {ref.influence}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </AccordionSection>
                    )}

                    {/* Processing Timeline */}
                    {processingTimeline.length > 0 && (
                        <AccordionSection
                            title="Processing Timeline"
                            subtitle="Agent execution sequence and timing"
                            icon="⏱️"
                            count={processingTimeline.length}
                            isExpanded={expandedSections.processingTimeline}
                            onToggle={() => toggleSection('processingTimeline')}
                        >
                            <div className="timeline-content">
                                {totalProcessingTime && (
                                    <div className="total-time-summary">
                                        <strong>Total Processing Time:</strong> {(totalProcessingTime / 1000).toFixed(2)}s
                                    </div>
                                )}
                                <div className="timeline-items">
                                    {processingTimeline.map((item, idx) => {
                                        const durationSeconds = item.duration_ms ? (item.duration_ms / 1000).toFixed(2) : '0.00';
                                        const agentName = item.agent || item.agent_name || 'Unknown';
                                        return (
                                            <div key={idx} className="timeline-item">
                                                <div className="timeline-item-header">
                                                    <span className="timeline-agent">{agentName}</span>
                                                    <span className={`timeline-status ${item.success !== false ? 'success' : 'error'}`}>
                                                        {item.success !== false ? '✓' : '✗'}
                                                    </span>
                                                    <span className="timeline-duration">{durationSeconds}s</span>
                                                </div>
                                                {item.description && (
                                                    <p className="timeline-description">{item.description}</p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </AccordionSection>
                    )}

                    {/* Agent Execution Details */}
                    {agentOutputs.length > 0 && (
                        <AccordionSection
                            title="Agent Execution Details"
                            subtitle="Outputs from each processing agent"
                            icon="🤖"
                            count={agentOutputs.length}
                            isExpanded={expandedSections.agentDetails}
                            onToggle={() => toggleSection('agentDetails')}
                        >
                            <div className="agent-details-content">
                                {agentOutputs.map((output, idx) => (
                                    <div key={idx} className="agent-output-item">
                                        <h5>{output.agent_name} ({output.agent_type})</h5>
                                        <p className="agent-summary">{output.output_summary}</p>
                                        {output.key_findings && output.key_findings.length > 0 && (
                                            <div className="agent-findings">
                                                <strong>Key Findings:</strong>
                                                <ul>
                                                    {output.key_findings.map((finding, fIdx) => (
                                                        <li key={fIdx}>{finding}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </AccordionSection>
                    )}

                    {/* Decision Points */}
                    {decisionPoints.length > 0 && (
                        <AccordionSection
                            title="Decision Points"
                            subtitle="Key decisions made during evaluation"
                            icon="🎯"
                            count={decisionPoints.length}
                            isExpanded={expandedSections.decisionPoints}
                            onToggle={() => toggleSection('decisionPoints')}
                        >
                            <div className="decision-points-content">
                                {decisionPoints.map((decision, idx) => (
                                    <div key={idx} className="decision-point-item">
                                        <h5>{decision.decision_type || 'Decision'}</h5>
                                        <p className="decision-reasoning">{decision.reasoning || decision.decision}</p>
                                        {decision.alternatives_considered && decision.alternatives_considered.length > 0 && (
                                            <div className="decision-alternatives">
                                                <strong>Alternatives Considered:</strong> {decision.alternatives_considered.join(', ')}
                                            </div>
                                        )}
                                        {decision.confidence !== undefined && (
                                            <div className="decision-confidence">
                                                Confidence: {(decision.confidence * 100).toFixed(1)}%
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </AccordionSection>
                    )}

                    {/* Enhanced Decisions */}
                    {(jdExtractionDecisions.length > 0 || evaluationModeDecision || scoreCalculationDetails || 
                      substitutabilityDecisions.length > 0 || parallelExecutionDecisions) && (
                        <AccordionSection
                            title="Enhanced Decision Details"
                            subtitle="Advanced explainability features"
                            icon="⚡"
                            isExpanded={expandedSections.enhancedDecisions}
                            onToggle={() => toggleSection('enhancedDecisions')}
                        >
                            <div className="enhanced-decisions-content">
                                {evaluationModeDecision && (
                                    <div className="enhanced-decision-item">
                                        <h5>Evaluation Mode: {evaluationModeDecision.selected_mode}</h5>
                                        <p>{evaluationModeDecision.reasoning}</p>
                                        <div className="decision-meta">
                                            Confidence: {(evaluationModeDecision.confidence * 100).toFixed(1)}%
                                        </div>
                                    </div>
                                )}

                                {jdExtractionDecisions.length > 0 && (
                                    <div className="enhanced-decision-item">
                                        <h5>JD Extraction Decisions ({jdExtractionDecisions.length})</h5>
                                        {jdExtractionDecisions.slice(0, 3).map((decision, idx) => (
                                            <div key={idx} className="jd-decision">
                                                <strong>{decision.requirement_text}</strong>
                                                <p>{decision.reasoning}</p>
                                            </div>
                                        ))}
                                        {jdExtractionDecisions.length > 3 && (
                                            <p className="more-items">+{jdExtractionDecisions.length - 3} more decisions</p>
                                        )}
                                    </div>
                                )}

                                {substitutabilityDecisions.length > 0 && (
                                    <div className="enhanced-decision-item">
                                        <h5>Substitutability Decisions ({substitutabilityDecisions.length})</h5>
                                        {substitutabilityDecisions.slice(0, 3).map((decision, idx) => (
                                            <div key={idx} className="sub-decision">
                                                <strong>{decision.candidate_skill} → {decision.required_skill}</strong>
                                                <p>{decision.reasoning}</p>
                                            </div>
                                        ))}
                                        {substitutabilityDecisions.length > 3 && (
                                            <p className="more-items">+{substitutabilityDecisions.length - 3} more decisions</p>
                                        )}
                                    </div>
                                )}

                                {parallelExecutionDecisions && (
                                    <div className="enhanced-decision-item">
                                        <h5>Parallel Execution</h5>
                                        <p>Time saved: {(parallelExecutionDecisions.time_saved_ms / 1000).toFixed(2)}s 
                                           ({(parallelExecutionDecisions.time_saved_percent).toFixed(1)}%)</p>
                                    </div>
                                )}
                            </div>
                        </AccordionSection>
                    )}
                </div>
            )}
        </div>
    );
};

export default ExplainabilityTab;
